import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import { captureException } from '@/errors';
import { useFlashesStore } from '@/stores/FlashesStore';
import { initStore } from '@/stores/store-utils';
import type { ApiKey, ApiKeyLimits } from '@/types/api_keys';

export const useApiKeysStore = defineStore('ApiKeys', () => {
  const apiKeys = ref<ApiKey[]>([]);
  const limits = ref<ApiKeyLimits | null>(null);
  const invalidKeyNames = ref<string[]>([]);
  const availableScopes = ref<string[]>([]);
  const lastRefreshedAt = ref<DateTime | null>(null);
  const flashesStore = useFlashesStore();

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      const { data, error } = await crossbeamApi.GET('/v0.1/api-keys');
      if (error) {
        captureException(error);
        flashesStore.addErrorFlash({ message: 'Failed to load API keys' });
        return;
      }
      apiKeys.value = data.keys;
      limits.value = data.limits;
      invalidKeyNames.value = data.invalid_key_names;
      availableScopes.value = data.available_scopes;
      lastRefreshedAt.value = DateTime.now();
    },
  );

  refresh({ useCache: true });

  const takenKeyNames = computed(() => [
    ...invalidKeyNames.value,
    ...apiKeys.value.map((apiKey) => apiKey.name),
  ]);

  function getApiKeyById(id: string) {
    return apiKeys.value.find((apiKey) => apiKey.id === id);
  }

  /** A revoked key's name stays taken, so it moves to the invalid list. */
  function removeApiKey(id: string) {
    const revokedKey = getApiKeyById(id);
    if (!revokedKey) return;
    invalidKeyNames.value = [...invalidKeyNames.value, revokedKey.name];
    apiKeys.value = apiKeys.value.filter((apiKey) => apiKey.id !== id);
  }

  async function revokeApiKey(id: string) {
    const { error } = await crossbeamApi.PUT('/v0.1/api-keys/{uuid}/revoke', {
      params: { path: { uuid: id } },
    });
    if (error) {
      captureException(error);
      flashesStore.addErrorFlash({ message: 'Failed to revoke API key' });
      return false;
    }
    removeApiKey(id);
    return true;
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    refreshApiKeysStore: refresh,
    apiKeys,
    limits,
    invalidKeyNames,
    takenKeyNames,
    availableScopes,
    lastRefreshedAt,
    getApiKeyById,
    removeApiKey,
    revokeApiKey,
  };
});
